// RamsRegisterTab — consolidated RAMS Sign-On Register: every request published
// to a client (acknowledged or awaiting acknowledgement) for compliance evidence.
// Sourced from existing RamsRequest publish/acknowledge fields — no separate records.
import { useCallback, useEffect, useState } from "react";
import { Printer, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, filenameFromResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Pill from "@/components/Pill";

const BRAND = "var(--brand-accent, #C75A1B)";
const INTERNAL = new Set(["director", "administrator", "consultant"]);

export default function RamsRegisterTab() {
    const { user } = useAuth();
    const isInternal = INTERNAL.has(user?.role);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [entries, setEntries] = useState([]);
    const [talks, setTalks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfBusy, setPdfBusy] = useState(false);
    const [f, setF] = useState({ client_id: "", project_id: "", template_id: "", status: "", date_from: "", date_to: "" });

    useEffect(() => {
        if (isInternal) api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    }, [isInternal]);

    useEffect(() => {
        if (!f.client_id) { setProjects([]); return; }
        api.get(`/projects?client_id=${f.client_id}`).then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
    }, [f.client_id]);

    const params = useCallback(() => {
        const p = {};
        if (f.client_id) p.client_id = f.client_id;
        if (f.project_id) p.project_id = f.project_id;
        if (f.template_id) p.template_id = f.template_id;
        if (f.status) p.status = f.status;
        if (f.date_from) p.date_from = f.date_from;
        if (f.date_to) p.date_to = f.date_to;
        return p;
    }, [f]);

    const load = useCallback(() => {
        setLoading(true);
        api.get("/rams/requests/register", { params: params() })
            .then((r) => { setEntries(r.data.entries || []); setTalks(r.data.talks || []); })
            .catch(() => setEntries([])).finally(() => setLoading(false));
    }, [params]);
    useEffect(load, [load]);

    async function generatePdf() {
        setPdfBusy(true);
        try {
            const res = await api.get("/rams/requests/register/pdf", { params: params(), responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = filenameFromResponse(res, "rams-sign-on-register.pdf");
            a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Could not generate PDF"); }
        setPdfBusy(false);
    }

    const fmt = (s) => { try { return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return s || "—"; } };
    const ackCount = entries.filter((e) => e.status === "Client Acknowledged").length;

    return (
        <div className="space-y-4" data-testid="rams-register-tab">
            <p className="text-[12.5px] text-[#6F6A64]">
                Every RAMS published to a client appears here automatically, with acknowledgement status — a single
                consolidated register of Title/Reference, Attendee, Company, Client, Project, Date/Time and status for compliance evidence.
            </p>
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div className="flex items-end gap-2 flex-wrap">
                    {isInternal && (
                        <FilterField label="Client">
                            <select data-testid="rams-register-filter-client" value={f.client_id}
                                onChange={(e) => setF({ ...f, client_id: e.target.value, project_id: "" })}
                                className="h-9 px-2.5 rounded-lg border text-[12.5px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                                <option value="">All clients</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </FilterField>
                    )}
                    <FilterField label="Project">
                        <select data-testid="rams-register-filter-project" value={f.project_id} onChange={(e) => setF({ ...f, project_id: e.target.value })}
                            disabled={isInternal && !f.client_id}
                            className="h-9 px-2.5 rounded-lg border text-[12.5px] bg-white disabled:opacity-50" style={{ borderColor: "#E5E0DA" }}>
                            <option value="">All projects</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </FilterField>
                    <FilterField label="RAMS">
                        <select data-testid="rams-register-filter-template" value={f.template_id} onChange={(e) => setF({ ...f, template_id: e.target.value })}
                            className="h-9 px-2.5 rounded-lg border text-[12.5px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                            <option value="">All RAMS</option>
                            {talks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </FilterField>
                    <FilterField label="Status">
                        <select data-testid="rams-register-filter-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
                            className="h-9 px-2.5 rounded-lg border text-[12.5px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                            <option value="">All statuses</option>
                            <option value="Client Acknowledged">Client Acknowledged</option>
                            <option value="Published to Client">Awaiting acknowledgement</option>
                        </select>
                    </FilterField>
                    <FilterField label="From">
                        <input data-testid="rams-register-filter-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })}
                            className="h-9 px-2.5 rounded-lg border text-[12.5px] bg-white" style={{ borderColor: "#E5E0DA" }} />
                    </FilterField>
                    <FilterField label="To">
                        <input data-testid="rams-register-filter-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })}
                            className="h-9 px-2.5 rounded-lg border text-[12.5px] bg-white" style={{ borderColor: "#E5E0DA" }} />
                    </FilterField>
                    <button data-testid="rams-register-refresh" onClick={load} className="h-9 px-2.5 rounded-lg border text-[#6F6A64] hover:text-[#1F1F1F]" style={{ borderColor: "#E5E0DA" }} title="Refresh"><RefreshCw size={14} /></button>
                </div>
                <button data-testid="rams-register-generate-pdf" onClick={generatePdf} disabled={pdfBusy || loading}
                    className="text-[13px] font-bold rounded-full px-4 py-2.5 text-white inline-flex items-center gap-2 disabled:opacity-60" style={{ background: BRAND }}>
                    {pdfBusy ? <Loader2 size={15} className="animate-spin" /> : <Printer size={15} />}
                    Generate PDF / Print Register
                </button>
            </div>

            {loading ? (
                <div className="text-center py-16"><Loader2 className="animate-spin inline" size={22} /></div>
            ) : entries.length === 0 ? (
                <div className="text-center text-[13px] text-[#9A968F] py-16 border border-dashed rounded-xl" style={{ borderColor: "#E5E0DA" }}>
                    No published RAMS match these filters yet.
                </div>
            ) : (
                <>
                    <p className="text-[11.5px] text-[#9A968F]">{entries.length} record{entries.length === 1 ? "" : "s"} · {ackCount} acknowledged</p>
                    <div className="bg-white border rounded-[13px] overflow-hidden shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                        <table className="w-full text-[12.5px]" data-testid="rams-register-table">
                            <thead><tr className="text-left text-[#9A968F] border-b" style={{ borderColor: "#F0EDE8" }}>
                                <th className="px-4 py-2.5 font-semibold">RAMS Title / Reference</th>
                                <th className="px-4 py-2.5 font-semibold">Version</th>
                                <th className="px-4 py-2.5 font-semibold">Attendee</th>
                                <th className="px-4 py-2.5 font-semibold">Company</th>
                                <th className="px-4 py-2.5 font-semibold">Client</th>
                                <th className="px-4 py-2.5 font-semibold">Project / Site</th>
                                <th className="px-4 py-2.5 font-semibold">Date / Time</th>
                                <th className="px-4 py-2.5 font-semibold">Status</th>
                            </tr></thead>
                            <tbody>
                                {entries.map((e, i) => (
                                    <tr key={`${e.request_id}-${i}`} data-testid={`rams-register-row-${i}`} className="border-b last:border-b-0" style={{ borderColor: "#F5F3F0" }}>
                                        <td className="px-4 py-2.5 font-semibold text-[#1F1F1F]">{e.rams_title} <span className="text-[#9A968F] font-normal">({e.reference})</span></td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{e.version ?? "—"}</td>
                                        <td className="px-4 py-2.5 text-[#1F1F1F]">{e.attendee}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{e.company}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{e.client_name}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{e.project_or_site}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{fmt(e.date)}</td>
                                        <td className="px-4 py-2.5"><Pill tone={e.status === "Client Acknowledged" ? "green" : "amber"}>{e.status}</Pill></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

function FilterField({ label, children }) {
    return (
        <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[#9A968F]">{label}</span>
            {children}
        </div>
    );
}
